import api from './api';
import { handleApiError } from '../utils/error-utils';
import { AxiosResponse } from 'axios';
import { Opportunity as BaseOpportunity, Quote as BaseQuote, Order, Product, User } from '../types';
import API_CONFIG from '../config/api';
import axios from 'axios';

// Common interface for paginated responses
interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page?: number;
  limit?: number;
}

// Extended Opportunity interface for admin view
export interface AdminOpportunity extends BaseOpportunity {
  total_amount: number;
  customer_name: string;
  reseller_name: string;
}

// Extended Quote interface for admin view
export interface AdminQuote extends BaseQuote {
  reference_number: string;
  total_amount: number;
  created_at: string;
  customer_name: string;
  reseller_name: string;
}

// Admin Quote Details interface with items
export interface AdminQuoteWithItems {
  id: string;
  code: string;
  created_at: string;
  reseller_id: string;
  customer_id: string;
  payment_method: string;
  payment_term: string;
  shipping_address_id: string;
  status: string;
  expiration_date: string;
  santander_proposal_number: string | null;
  is_syncing_with_santander: boolean | null;
  observation?: string;
  customer: {
    id: string;
    company_name: string;
    email: string;
    phone: string;
    cnpj: string;
    sector: string;
    state_registration: string | null;
    reseller_id: string;
    created_at: string;
    reseller: {
      id: string;
      name: string;
      email: string;
    };
  };
  reseller: {
    id: string;
    name: string;
    email: string;
    phone: string;
  };
  shipping_address: {
    street: string;
    number: string | null;
    city: string;
    state: string;
    zip_code: string;
    country: string;
    is_default: boolean;
    complement: string | null;
    is_billing: boolean;
    is_delivery: boolean;
    billing: boolean;
    id: string;
    customer_id: string;
  };
  quote_items: Array<{
    id: string;
    quote_id: string;
    product_id: string;
    quantity: number;
    unit_price: number;
    discount_percent: number;
    discount_amount: number;
    cisp1_commission: number;
    reseller_commission: number;
    final_price: number | null;
    product: {
      id: string;
      sku: string;
      name: string;
      description: string;
      price: number;
      is_active: boolean;
      specifications: Record<string, any>;
      manual?: string;
      cisp1_commission: number;
      reseller_commission: number;
      images?: Array<{
        id: string;
        image_url: string;
        alt_text: string;
        is_primary: boolean;
        display_order: number;
        product_id: string;
      }>;
    };
  }>;
}

export interface SyncCreditAnalysisResponse {
  quote_id: string;
  santander_proposal_number: string;
  status: boolean;
  message: string;
}

// Admin Quote Item interface
export interface AdminQuoteItem {
  id: string;
  quote_id: string;
  product_id: string;
  product: Product;
  quantity: number;
  unit_price: number;
  discount_percent: number;
  discount_amount: number;
  cisp1_commission: number;
  reseller_commission: number;
  final_price: number | null;
}

// Extended Order interface for admin view
export interface AdminOrder extends Omit<Order, 'status' | 'items'> {
  customer_name: string;
  reseller_name: string;
  reference_number?: string;
  total_amount?: number;
  created_at?: string;
  status: string; // Override the status type to be more flexible for admin view
  quote?: {
    id: string;
    code: string;
    status: string;
    expiration_date: string;
    reseller_id: string;
    created_at: string;
    reseller: {
      id: string;
      name: string;
      email: string;
    };
  };
  customer?: {
    company_name: string;
    phone: string;
    email: string;
    cnpj: string;
    sector: string;
    state_registration?: string | null;
    id: string;
    reseller_id: string;
    created_at: string;
    reseller: {
      id: string;
      name: string;
      email: string;
    };
  };
  shipping_address?: {
    street: string;
    number?: string | null;
    city: string;
    state: string;
    zip_code: string;
    country: string;
    is_default: boolean;
    complement?: string | null;
    is_billing?: boolean | null;
    is_delivery?: boolean | null;
    id: string;
    customer_id: string;
  };
  order_items: AdminOrderItem[];
}

// Interface for order items
export interface AdminOrderItem {
  id: string;
  order_id: string;
  product_id: string;
  quantity: number;
  unit_price: number;
  discount_percent: number;
  discount_amount: number;
}

// OrderItem interface for regular orders
export interface OrderItem {
  id: string;
  order_id: string;
  product_id: string;
  product: Product;
  quantity: number;
  unit_price: number;
}

// Extended Product interface for admin view
export interface AdminProduct extends Product {
  created_at?: string;
  updated_at?: string;
  cisp1_commission: number;
  reseller_commission: number;
  price_with_tax?: number;
  use_acer_empresas_price_rule?: boolean;
  acer_empresas_net_price?: number | null;
  specifications?: Record<string, any>;
  manual?: string;
  images?: Array<{
    id: string;
    image_url: string;
    alt_text?: string;
    is_primary: boolean;
    display_order: number;
    product_id: string;
  }>;
}

export type TemplateType = 'image' | 'html' | 'text';

export interface AdminTemplate {
  id: string;
  author: string;
  author_email: string;
  title: string;
  description: string;
  template_type: TemplateType;
}

export interface TemplateProductInfo {
  id: string;
  name: string;
  sku: string;
  image: string;
}

export interface AdminTemplateDetails {
  id: string;
  author: string;
  author_email: string;
  title: string;
  description: string;
  template_type: TemplateType;
  features: string;
  target_audience: string;
  products: TemplateProductInfo[];
  instructions?: string | null;
  content: string;
  copywriting_title: string;
  copywriting_subtitle: string;
  copywriting_description: string;
  promotional_image: string;
  created_at: string;
  content_url_for_download?: string | null;
}

export interface AdminCampaign {
  template_id: string;
  title: string;
  description: string;
  target_audience: string;
}

export interface AdminCampaignDetails {
  template_id: string;
  id: string;
  reseller_id?: string;
  title: string;
  description: string;
  target_audience: string;
  url: string;
  reseller_name: string;
  reseller_email: string;
  started_chats: number;
  leads_count: number;
  requested_quotes: number;
  content: string;
  template_type: TemplateType;
  created_at: string;
}

export interface AdminCampaignReseller {
  id: string;
  reseller_name: string;
  reseller_email: string;
  started_chats: number;
  leads_count: number;
  requested_quotes: number;
  created_at: string;
}

export interface AdminCampaignResellersResponse {
  template_id: string;
  title: string;
  description: string;
  target_audience: string;
  items: AdminCampaignReseller[];
  total: number;
}

export interface AdminCampaignChat {
  id: string;
  campaign_id: string | null;
  identification?: string | null;
  is_lead?: boolean;
  request_quote?: boolean;
  messages: number | null;
  follow_up_provided?: boolean | null;
  has_new_messages?: boolean;
  created_at: string;
}

export interface AdminCampaignChatMessage {
  id: string;
  message: string;
  role: string;
  created_at: string;
}

export interface AdminCampaignChatProductInfo {
  id: string;
  name: string;
  sku: string;
  image: string;
  quantity?: number | null;
}

export interface AdminCampaignChatDetails {
  id: string;
  messages: AdminCampaignChatMessage[];
  identification?: string | null;
  is_lead?: boolean;
  request_quote?: boolean;
  cnpj?: string | null;
  contact?: string | null;
  address?: string | null;
  budget?: string | null;
  authority?: string | null;
  need?: string | null;
  timeframe?: string | null;
  products?: AdminCampaignChatProductInfo[] | null;
  created_at: string;
  updated_at?: string | null;
  has_new_messages?: boolean;
  follow_up_provided?: boolean | null;
}

export interface GenerateTemplateRequest {
  title: string;
  description: string;
  template_type: TemplateType;
  features: string;
  target_audience: string;
  products_id: string[];
  instructions?: string | null;
}

export interface EnrichPromptGenerateTemplateContentResponse {
  prompt: string;
}

export interface GenerateTemplateContentResponse {
  content: string;
}

export interface GenerateTemplateCopywritingResponse {
  title: string;
  subtitle: string;
  description: string;
}

export interface GenerateTemplatePromotionalImageResponse {
  promotional_image: string;
}

export interface CreateTemplateRequest {
  title: string;
  description: string;
  template_type: TemplateType;
  features: string;
  target_audience: string;
  products_id: string[];
  instructions?: string | null;
  content: string;
  copywriting_title: string;
  copywriting_subtitle: string;
  copywriting_description: string;
  promotional_image: string;
}

export interface CreateTemplateResponse {
  id: string;
}

export interface DeleteTemplateResponse {
  id: string;
}

export interface GenerateCampaignsResponse {
  template_id: string;
}

// Quote Price Change Log interface
export interface QuotePriceChangeLog {
  id: string;
  quote_item_id: string;
  admin_id: string;
  previous_quantity: number;
  new_quantity: number;
  previous_price: number;
  new_price: number;
  created_at: string;
  admin: {
    id: string;
    name: string;
    email: string;
  };
}

// Interface para a resposta da API de logs de alteração de preço
export interface PriceChangeLogListResponse {
  items: QuotePriceChangeLog[];
  total: number;
}

// Interface para a resposta completa da API
export interface ApiPriceChangeLogResponse {
  data: PriceChangeLogListResponse;
}

// Opportunities
export const getAdminOpportunities = async (
  page = 1,
  limit = 10
): Promise<PaginatedResponse<AdminOpportunity> | null> => {
  try {
    const response: AxiosResponse<PaginatedResponse<AdminOpportunity>> = await api.get(
      `/admin/opportunities/?skip=${(page - 1) * limit}&limit=${limit}`
    );
    return response.data;
  } catch (error) {
    handleApiError(error);
    return null;
  }
};

// Quotes
export const getAdminQuotes = async (
  page = 1,
  limit = 10
): Promise<PaginatedResponse<AdminQuote> | null> => {
  try {
    const response: AxiosResponse<PaginatedResponse<AdminQuote>> = await api.get(
      `/admin/quotes/?skip=${(page - 1) * limit}&limit=${limit}`
    );
    return response.data;
  } catch (error) {
    handleApiError(error);
    return null;
  }
};

// Get a specific quote with items for admin
export const getAdminQuoteWithItems = async (
  quoteId: string
): Promise<AdminQuoteWithItems | null> => {
  try {
    console.log(`Fetching quote with ID: ${quoteId}`);

    // The api.get method already extracts the data property from the response
    const quoteData = await api.get<AdminQuoteWithItems>(`/quotes/${quoteId}/with-items`);

    console.log('Quote data received:', quoteData);

    return quoteData;
  } catch (error) {
    console.error('Error fetching quote details:', error);
    handleApiError(error);
    return null;
  }
};

export const adminUpdateQuoteObservation = async (
  quoteId: string,
  observation: string
): Promise<AdminQuoteWithItems | null> => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.patch(
      `${API_CONFIG.BASE_URL_WITH_VERSION}/admin/quotes/${quoteId}`,
      { observation },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data as AdminQuoteWithItems;
  } catch (error) {
    handleApiError(error);
    return null;
  }
};

export const adminUpdateQuoteFields = async (
  quoteId: string,
  payload: Partial<{
    payment_method: string;
    payment_term: string;
    observation: string;
    expiration_date: string;
    shipping_address_id: string;
    status: string;
  }>
): Promise<AdminQuoteWithItems | null> => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.patch(
      `${API_CONFIG.BASE_URL_WITH_VERSION}/admin/quotes/${quoteId}`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data as AdminQuoteWithItems;
  } catch (error) {
    handleApiError(error);
    return null;
  }
};

export const syncQuoteCreditAnalysis = async (
  quoteId: string,
  payload: {
    santander_proposal_number: string;
  }
): Promise<SyncCreditAnalysisResponse | null> => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.patch(
      `${API_CONFIG.BASE_URL_WITH_VERSION}/admin/quotes/${quoteId}/credit_analysis/sync`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data as SyncCreditAnalysisResponse;
  } catch (error) {
    handleApiError(error);
    return null;
  }
};

// Update a quote item's unit price (admin only)
export const updateAdminQuoteItemPrice = async (
  quoteItemId: string,
  unitPrice: number,
  quantity: number
): Promise<AdminQuoteItem | null> => {
  try {
    // Use the admin-specific endpoint for updating quote item prices
    const response = await api.patch<
      AdminQuoteItem,
      {
        unit_price: number;
        quantity: number;
      }
    >(`/admin/quotes/items/${quoteItemId}`, {
      unit_price: unitPrice,
      quantity: quantity,
    });
    return response;
  } catch (error) {
    console.error('Error updating quote item:', error);
    handleApiError(error);
    return null;
  }
};

// Get admin orders with pagination
export const getAdminOrders = async (
  skip = 0,
  limit = 10
): Promise<PaginatedResponse<AdminOrder> | null> => {
  try {
    const url = `/admin/orders/?skip=${skip}&limit=${limit}`;
    const response = await api.get<PaginatedResponse<AdminOrder>>(url);
    return response;
  } catch (error) {
    handleApiError(error);
    return null;
  }
};

// Get Acer status for an order (admin only)
export interface AcerNotaFiscal {
  statusCodigo?: string;
  statusDescricao?: string;
  numeroNota?: string;
  dataFaturamento?: string;
  dataEnvioSFC?: string;
  dataExpedicao?: string;
  dataEntrega?: string;
  codigoRastreio?: string;
  xml?: string; // base64
}

export interface AcerStatusResponse {
  status: string; // e.g., SRT01 or ECT02
  mensagem: string;
  retorno?: {
    id?: string; // ACR code
    numeroPedidoCli?: string;
    statusCodigo?: string;
    statusDescricao?: string; // INTEGRADO / ENTREGUE etc.
    datanf?: AcerNotaFiscal[]; // New structure: list of NF entries
    numeroNota?: string;
    dataFaturamento?: string;
    dataEnvioSFC?: string;
    dataExpedicao?: string;
    dataEntrega?: string;
    codigoRastreio?: string;
    xml?: string; // base64
  };
}

export const getAdminOrderAcerStatus = async (
  orderId: string,
  opportunityId?: string
): Promise<AcerStatusResponse | null> => {
  try {
    const params = opportunityId ? `?opportunity_id=${encodeURIComponent(opportunityId)}` : '';
    const url = `/admin/orders/${orderId}/acer-status${params}`;
    const response = await api.get<AcerStatusResponse>(url);
    return response;
  } catch (error) {
    handleApiError(error as any);
    return null;
  }
};

// Products
export const getAdminProducts = async (
  page = 1,
  limit = 10
): Promise<PaginatedResponse<AdminProduct> | null> => {
  try {
    const response = await api.get<PaginatedResponse<AdminProduct>>(
      `/admin/products/?skip=${(page - 1) * limit}&limit=${limit}`
    );
    return response;
  } catch (error) {
    handleApiError(error);
    return null;
  }
};

export const getAdminProductById = async (id: string): Promise<AdminProduct | null> => {
  try {
    // Get the auth token
    const token = localStorage.getItem('token');

    // Use axios directly instead of our custom API client
    // Updated to use the new admin endpoint that returns commission values
    const response = await axios.get(
      `${API_CONFIG.BASE_URL_WITH_VERSION}/admin/products/${id}/admin`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (response.status === 200 && response.data) {
      return response.data;
    }

    return null;
  } catch (error) {
    handleApiError(error);
    return null;
  }
};

export const updateAdminProduct = async (
  id: string,
  productData: {
    price?: number;
    cisp1_commission?: number;
    reseller_commission?: number;
    use_acer_empresas_price_rule?: boolean;
    acer_empresas_net_price?: number;
    is_active?: boolean;
    manual?: string;
  }
): Promise<AdminProduct | null> => {
  try {
    // Get the auth token
    const token = localStorage.getItem('token');

    // Use axios directly instead of our custom API client
    // Updated to use the new admin endpoint that returns commission values
    const response = await axios.patch(
      `${API_CONFIG.BASE_URL_WITH_VERSION}/admin/products/${id}/admin`,
      productData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (response.status === 200 && response.data) {
      return response.data;
    }

    return null;
  } catch (error) {
    handleApiError(error);
    return null;
  }
};

// Templates
export const getAdminTemplates = async (
  page = 1,
  limit = 10
): Promise<PaginatedResponse<AdminTemplate> | null> => {
  try {
    const response = await api.get<PaginatedResponse<AdminTemplate>>(
      `/templates/?skip=${(page - 1) * limit}&limit=${limit}`
    );
    return response;
  } catch (error) {
    handleApiError(error);
    return null;
  }
};

export const getAdminCampaigns = async (
  skip = 0,
  limit = 10
): Promise<PaginatedResponse<AdminCampaign> | null> => {
  try {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
      sort: 'created_at:desc',
    });

    const response = await api.get<PaginatedResponse<AdminCampaign>>(
      `/campaigns/?${params.toString()}`
    );
    return response;
  } catch (error) {
    handleApiError(error);
    return null;
  }
};

export const getAdminCampaignDetails = async (
  campaignId: string
): Promise<AdminCampaignDetails | null> => {
  try {
    const response = await api.get<AdminCampaignDetails>(`/campaigns/${campaignId}`);
    return response;
  } catch (error) {
    handleApiError(error);
    return null;
  }
};

export const getAdminCampaignChats = async (
  campaignId: string,
  skip = 0,
  limit = 10
): Promise<PaginatedResponse<AdminCampaignChat> | null> => {
  try {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
      sort: 'created_at:desc',
      campaign_id: campaignId,
    });

    const response = await api.get<PaginatedResponse<AdminCampaignChat>>(
      `/campaigns/chats?${params.toString()}`
    );
    return response;
  } catch (error) {
    handleApiError(error);
    return null;
  }
};

export const getAdminCampaignResellers = async (
  templateId: string,
  skip = 0,
  limit = 10,
  withActivities = true
): Promise<AdminCampaignResellersResponse | null> => {
  try {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
      sort: 'created_at:desc',
      with_activities: withActivities.toString(),
    });

    const response = await api.get<AdminCampaignResellersResponse>(
      `/campaigns/${encodeURIComponent(templateId)}/resellers?${params.toString()}`
    );
    return response;
  } catch (error) {
    handleApiError(error);
    return null;
  }
};

export const getAdminCampaignChatDetails = async (
  chatId: string
): Promise<AdminCampaignChatDetails | null> => {
  try {
    const response = await api.get<AdminCampaignChatDetails>(`/campaigns/chats/${chatId}`);
    return response;
  } catch (error) {
    handleApiError(error);
    return null;
  }
};

export const markAdminCampaignChatAsRead = async (chatId: string): Promise<boolean | null> => {
  try {
    const response = await api.get<boolean>(
      `/campaigns/chats/${encodeURIComponent(chatId)}/mark-as-read`
    );
    return response;
  } catch (error) {
    handleApiError(error);
    return null;
  }
};

export const getAdminTemplateDetails = async (
  templateId: string
): Promise<AdminTemplateDetails | null> => {
  try {
    const response = await api.get<AdminTemplateDetails>(`/templates/${templateId}`);
    return response;
  } catch (error) {
    handleApiError(error);
    return null;
  }
};

export const enrichTemplatePrompt = async (
  payload: GenerateTemplateRequest
): Promise<EnrichPromptGenerateTemplateContentResponse | null> => {
  try {
    const response = await api.post<
      EnrichPromptGenerateTemplateContentResponse,
      GenerateTemplateRequest
    >('/templates/generate/enrich-prompt', payload);
    return response;
  } catch (error) {
    handleApiError(error);
    return null;
  }
};

const cleanPromptValue = (value: string): string => {
  let normalized = value.trim();

  // Remove fechamento de objeto quando vier no formato {prompt='...'}
  normalized = normalized.replace(/\}$/, '').trim();

  // Remove aspas de abertura/fechamento (simples ou dupla)
  normalized = normalized.replace(/^['"]/, '').replace(/['"]$/, '');

  // Converte sequências escapadas em caracteres reais de formatação
  normalized = normalized
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '\r')
    .replace(/\\t/g, '\t');

  return normalized;
};

const decodeEscapedFormatting = (value: string): string =>
  value
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '\r')
    .replace(/\\t/g, '\t');

const extractStringFromJson = (json: unknown, key: 'prompt' | 'content'): string => {
  if (typeof json === 'string') return cleanPromptValue(json);
  if (json && typeof json === 'object' && key in json) {
    const value = (json as Record<'prompt' | 'content', unknown>)[key];
    if (typeof value === 'string') return cleanPromptValue(value);
  }
  return '';
};

const isDoneMarker = (value: string): boolean => value.trim().toUpperCase() === '[DONE]';

const takeNextSseBlock = (source: string): { block: string; rest: string } | null => {
  const separatorMatch = source.match(/\r?\n\r?\n/);
  if (!separatorMatch || separatorMatch.index === undefined) {
    return null;
  }

  const separatorIndex = separatorMatch.index;
  const separatorLength = separatorMatch[0].length;

  return {
    block: source.slice(0, separatorIndex),
    rest: source.slice(separatorIndex + separatorLength),
  };
};

const parseSseBlock = (rawBlock: string): { event: string; data: string } | null => {
  if (!rawBlock) {
    return null;
  }

  const lines = rawBlock.split(/\r?\n/);
  let eventName = 'message';
  const dataLines: string[] = [];

  for (const line of lines) {
    if (line.startsWith(':')) {
      continue;
    }

    if (line.startsWith('event:')) {
      eventName = line.slice('event:'.length).trim() || 'message';
      continue;
    }

    if (line.startsWith('data:')) {
      dataLines.push(line.startsWith('data: ') ? line.slice(6) : line.slice(5));
      continue;
    }

    dataLines.push(line);
  }

  if (!dataLines.length) {
    return null;
  }

  return {
    event: eventName,
    data: dataLines.join('\n'),
  };
};

const readStreamAsConcatenatedText = async (
  response: Response,
  onUpdate: (value: string) => void
): Promise<string> => {
  if (!response.body) return '';

  const contentType = response.headers.get('content-type') || '';
  const isSse = contentType.includes('text/event-stream');
  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');

  let accumulated = '';
  let buffer = '';
  let shouldContinueReading = true;
  let hasReceivedContent = false;

  while (shouldContinueReading) {
    const { value, done } = await reader.read();
    if (done) {
      shouldContinueReading = false;
      continue;
    }
    if (!value) continue;

    const decoded = decoder.decode(value, { stream: true });
    if (!decoded) continue;

    if (!isSse) {
      accumulated += decoded;
      hasReceivedContent = hasReceivedContent || decoded.length > 0;
      onUpdate(accumulated);
      continue;
    }

    buffer += decoded;
    let nextBlock = takeNextSseBlock(buffer);

    while (nextBlock) {
      // Em alguns streams há blocos SSE vazios para representar quebra de linha.
      if (nextBlock.block === '') {
        if (hasReceivedContent) {
          accumulated += '\n';
          onUpdate(accumulated);
        }
        buffer = nextBlock.rest;
        nextBlock = takeNextSseBlock(buffer);
        continue;
      }

      const parsedEvent = parseSseBlock(nextBlock.block);
      buffer = nextBlock.rest;

      if (!parsedEvent) {
        nextBlock = takeNextSseBlock(buffer);
        continue;
      }

      if (parsedEvent.event === 'done' || isDoneMarker(parsedEvent.data)) {
        shouldContinueReading = false;
        break;
      }

      if (parsedEvent.event === 'message' && parsedEvent.data === '') {
        if (hasReceivedContent) {
          accumulated += '\n';
          onUpdate(accumulated);
        }
        nextBlock = takeNextSseBlock(buffer);
        continue;
      }

      accumulated += parsedEvent.data;
      hasReceivedContent = hasReceivedContent || parsedEvent.data.length > 0;
      onUpdate(accumulated);

      nextBlock = takeNextSseBlock(buffer);
    }
  }

  const finalDecoded = decoder.decode();

  if (!isSse) {
    if (finalDecoded) {
      accumulated += finalDecoded;
      onUpdate(accumulated);
    }
    return accumulated;
  }

  buffer += finalDecoded;
  if (buffer.trim()) {
    const parsedEvent = parseSseBlock(buffer);
    if (parsedEvent && parsedEvent.event !== 'done' && !isDoneMarker(parsedEvent.data)) {
      if (parsedEvent.event === 'message' && parsedEvent.data === '') {
        if (hasReceivedContent) {
          accumulated += '\n';
          onUpdate(accumulated);
        }
      } else {
        accumulated += parsedEvent.data;
        onUpdate(accumulated);
      }
    }
  }

  return accumulated;
};

export const streamEnrichTemplatePrompt = async (
  payload: GenerateTemplateRequest,
  onPromptUpdate: (prompt: string) => void
): Promise<string | null> => {
  try {
    const token = localStorage.getItem('token');
    const response = await fetch(
      `${API_CONFIG.BASE_URL_WITH_VERSION}/templates/generate/enrich-prompt`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream, application/x-ndjson, application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      return null;
    }

    // Fallback para resposta JSON tradicional
    const contentType = response.headers.get('content-type') || '';
    if (!response.body || contentType.includes('application/json')) {
      const json = (await response.json()) as unknown;
      const prompt = extractStringFromJson(json, 'prompt');
      if (prompt) onPromptUpdate(prompt);
      return prompt;
    }

    const accumulatedPrompt = await readStreamAsConcatenatedText(response, value => {
      onPromptUpdate(decodeEscapedFormatting(value));
    });

    const normalizedPrompt = decodeEscapedFormatting(accumulatedPrompt);
    return normalizedPrompt || null;
  } catch (error) {
    console.error('Error streaming enrich prompt:', error);
    return null;
  }
};

export const generateTemplateContent = async (
  payload: GenerateTemplateRequest,
  onContentUpdate?: (content: string) => void
): Promise<GenerateTemplateContentResponse | null> => {
  try {
    const shouldNormalizeTextContent = payload.template_type === 'text';
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_CONFIG.BASE_URL_WITH_VERSION}/templates/generate/content`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream, application/x-ndjson, application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      return null;
    }

    const contentType = response.headers.get('content-type') || '';
    if (!response.body || contentType.includes('application/json')) {
      const json = (await response.json()) as unknown;
      const rawContent = extractStringFromJson(json, 'content');
      const content = shouldNormalizeTextContent ? decodeEscapedFormatting(rawContent) : rawContent;
      if (content && onContentUpdate) onContentUpdate(content);
      return content ? { content } : null;
    }

    const accumulatedContent = await readStreamAsConcatenatedText(response, value => {
      if (!onContentUpdate) return;

      const normalizedValue = shouldNormalizeTextContent ? decodeEscapedFormatting(value) : value;
      onContentUpdate(normalizedValue);
    });

    const normalizedContent = shouldNormalizeTextContent
      ? decodeEscapedFormatting(accumulatedContent)
      : accumulatedContent;

    return normalizedContent ? { content: normalizedContent } : null;
  } catch (error) {
    console.error('Error streaming template content:', error);
    return null;
  }
};

export const generateTemplateCopywriting = async (
  payload: GenerateTemplateRequest
): Promise<GenerateTemplateCopywritingResponse | null> => {
  try {
    const response = await api.post<GenerateTemplateCopywritingResponse, GenerateTemplateRequest>(
      '/templates/generate/copywriting',
      payload
    );
    return response;
  } catch (error) {
    handleApiError(error);
    return null;
  }
};

export const generateTemplatePromotionalImage = async (
  payload: GenerateTemplateRequest
): Promise<GenerateTemplatePromotionalImageResponse | null> => {
  try {
    const response = await api.post<
      GenerateTemplatePromotionalImageResponse,
      GenerateTemplateRequest
    >('/templates/generate/promotional-image', payload);
    return response;
  } catch (error) {
    handleApiError(error);
    return null;
  }
};

export const createAdminTemplate = async (
  templateId: string,
  payload: CreateTemplateRequest
): Promise<CreateTemplateResponse | null> => {
  try {
    const response = await api.post<CreateTemplateResponse, CreateTemplateRequest>(
      '/templates/create',
      payload
    );
    return response;
  } catch (error) {
    // Fallback para o endpoint documentado no OpenAPI atual.
    if (
      axios.isAxiosError(error) &&
      (error.response?.status === 404 || error.response?.status === 405)
    ) {
      try {
        const fallbackResponse = await api.post<CreateTemplateResponse, CreateTemplateRequest>(
          '/admin/templates/create',
          payload
        );
        return fallbackResponse;
      } catch (fallbackError) {
        handleApiError(fallbackError);
        return null;
      }
    }

    handleApiError(error);
    return null;
  }
};

export const saveResellerLogo = async (userId: string, logoFile: Blob): Promise<User | null> => {
  try {
    const formData = new FormData();
    formData.append('reseller_logo', logoFile, 'reseller-logo.png');

    const response = await api.post<User, FormData>(
      `/users/${encodeURIComponent(userId)}/save-reseller-logo`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response;
  } catch (error) {
    // Fallback para rota legada usada por algumas versões do backend.
    if (
      axios.isAxiosError(error) &&
      (error.response?.status === 404 || error.response?.status === 405)
    ) {
      try {
        const formData = new FormData();
        formData.append('reseller_logo', logoFile, 'reseller-logo.png');

        const fallbackResponse = await api.post<User, FormData>(
          '/users/save-reseller-logo',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        return fallbackResponse;
      } catch (fallbackError) {
        handleApiError(fallbackError);
        return null;
      }
    }

    handleApiError(error);
    return null;
  }
};

export const deleteAdminTemplate = async (
  templateId: string
): Promise<DeleteTemplateResponse | null> => {
  try {
    const response = await api.del<DeleteTemplateResponse>(`/templates/${templateId}`);
    return response;
  } catch (error) {
    handleApiError(error);
    return null;
  }
};

export const generateAdminTemplateCampaign = async (
  templateId: string
): Promise<GenerateCampaignsResponse | null> => {
  try {
    const response = await api.post<GenerateCampaignsResponse, Record<string, never>>(
      `/templates/${templateId}/generate-campaign`,
      {}
    );
    return response;
  } catch (error) {
    handleApiError(error);
    return null;
  }
};

// Users
export const getAdminUsers = async (
  page = 1,
  limit = 10
): Promise<PaginatedResponse<User> | null> => {
  try {
    console.log(`Calling admin users API with skip=${(page - 1) * limit}, limit=${limit}`);
    const response = await api.get<PaginatedResponse<User>>(
      `/admin/users/?skip=${(page - 1) * limit}&limit=${limit}&sort=created_at:desc`
    );
    console.log('Raw admin users API response structure:', {
      response,
      hasItems: response && 'items' in response,
      itemsLength: response && 'items' in response ? response.items.length : 0,
    });
    return response;
  } catch (error) {
    console.error('Error in getAdminUsers:', error);
    handleApiError(error);
    return null;
  }
};

// Get price change logs for a quote
export const getQuotePriceChangeLogs = async (
  quoteId: string,
  page = 1,
  limit = 10
): Promise<PriceChangeLogListResponse | null> => {
  try {
    // Usando a interface AxiosResponse com ApiPriceChangeLogResponse
    const response: AxiosResponse<ApiPriceChangeLogResponse> = await api.get(
      `/admin/quotes/${quoteId}/price-change-logs?skip=${(page - 1) * limit}&limit=${limit}`
    );
    // Acessando a propriedade data.data que contém a PriceChangeLogListResponse
    return response.data.data;
  } catch (error) {
    handleApiError(error);
    return null;
  }
};

// Get price change logs for a specific quote item
export const getQuoteItemPriceChangeLogs = async (
  quoteItemId: string,
  page = 1,
  limit = 10
): Promise<PriceChangeLogListResponse | null> => {
  try {
    // Usando a interface AxiosResponse com ApiPriceChangeLogResponse
    const response: AxiosResponse<ApiPriceChangeLogResponse> = await api.get(
      `/admin/quote-items/${quoteItemId}/price-change-logs?skip=${(page - 1) * limit}&limit=${limit}`
    );
    // Acessando a propriedade data.data que contém a PriceChangeLogListResponse
    return response.data.data;
  } catch (error) {
    handleApiError(error);
    return null;
  }
};

// Add search products function for admin
export const searchAdminProducts = async (
  searchTerm: string,
  skip = 0,
  limit = 10,
  sort?: string
): Promise<{ items: Product[]; total: number }> => {
  try {
    const params = new URLSearchParams({
      search: searchTerm,
      skip: skip.toString(),
      limit: limit.toString(),
    });

    if (sort) {
      params.append('sort', sort);
    }

    const response = await api.get<{ items: Product[]; total: number }>(
      `/admin/products/?${params.toString()}`
    );
    return response;
  } catch (error) {
    console.error('Error searching admin products:', error);
    throw error;
  }
};
